// SCR-COOP-CHAT, MOD-CHAT-MESSAGE, MOD-TG-GROUP (E08-T03; ux/07 §11–12, ADR-0028). Канал — coop-<coopId> (Р-68).
// Лента: история 7 дней с подгрузкой вверх по 50, новые — WS chat.message, скрытие — chat.hidden. Отправка — с clientMsgId:
// пузырь сразу, без сети — в очереди с часиками, через 30 с без сети — «!». Не чаще 1 сообщения в 2 с.
// Ссылки в тексте не кликабельны (ADR-0028). Закреплённое — Coop.pinnedMessage, «Закрепить» — PATCH /coops/{id} (Р-87).
import { signal } from '@preact/signals';
import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import type { ChatMessage } from '@garages/contract';
import { useApp } from '../app/app.js';
import { isBlocked } from '../content/word-filter.js';
import { mskTime } from '../i18n/format.js';
import { t as tr } from '../i18n/t.js';
import { ApiCallError, uuid7 } from '../net/api-client.js';
import { onWs } from '../net/ws.js';
import { coopBrief, isLeader, myCoop } from '../state/coop.js';
import { online, player } from '../state/game.js';
import { storage } from '../state/storage.js';
import { chatRulesAccepted, chatRulesVersion, legalUrls, markChatRulesAccepted } from '../state/legal.js';
import type { ScreenProps, SheetProps } from '../ui/App.js';
import { ScreenTitle, Sheet, Skeleton, useActionBar, useNow } from '../ui/kit.js';
import { showToast } from '../ui/toast.js';

export const CHAT_PAGE = 50;
export const CHAT_MAX = 500;
export const CHAT_COUNTER_FROM = 450;
export const CHAT_GAP_MS = 2000;
export const OFFLINE_FAIL_MS = 30_000;
export const QUICK_REPLIES = ['CL-CHAT-QUICK-1', 'CL-CHAT-QUICK-2', 'CL-CHAT-QUICK-3', 'CL-CHAT-QUICK-4', 'CL-CHAT-QUICK-5', 'CL-CHAT-QUICK-6'];

type Status = 'sent' | 'sending' | 'queued' | 'failed';
export interface Bubble { m: ChatMessage; status: Status; queuedAt?: number }

/** Ответ на сообщение и жалобы этого устройства: общие для экрана и листа сообщения. */
export const replyTo = signal<ChatMessage | null>(null);
const reportedLocal = signal<Set<string>>(new Set());
const muteLocal = signal<string | null>(null);

export const channelOf = (coopId: string) => `coop-${coopId}`;
const draftKey = (ch: string) => `chatDraft:${ch}`;

/** Ссылка-приглашение в группу (ux/07 §12): только t.me/+… и t.me/joinchat/…; приводится к https://. */
export function normalizeTgInvite(raw: string): string | null {
  const m = /^(?:https?:\/\/)?t\.me\/(\+[A-Za-z0-9_-]{6,}|joinchat\/[A-Za-z0-9_-]{6,})\/?$/.exec(raw.trim());
  return m ? `https://t.me/${m[1]}` : null;
}

/** Слияние ленты: по id, отправленное своё заменяет пузырь с тем же clientMsgId; порядок — по времени. */
export function mergeMessages(list: Bubble[], incoming: ChatMessage[], status: Status = 'sent'): Bubble[] {
  const out = list.slice();
  for (const m of incoming) {
    const i = out.findIndex((b) => b.m.id === m.id || (!!m.clientMsgId && b.m.clientMsgId === m.clientMsgId));
    if (i >= 0) out[i] = { m, status }; else out.push({ m, status });
  }
  return out.sort((a, b) => Date.parse(a.m.createdAt) - Date.parse(b.m.createdAt));
}

const nowIso = () => new Date().toISOString();

export function CoopChatScreen(_p: ScreenProps) {
  const app = useApp();
  const coop = coopBrief.value;
  const ch = channelOf(coop?.coopId ?? '0');
  const me = player.value?.publicId ?? '';
  const [list, setList] = useState<Bubble[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [more, setMore] = useState(true);
  const [err, setErr] = useState(false);
  const [mute, setMute] = useState<string | null>(null);
  const [tg, setTg] = useState<string | null>(null);
  const [text, setText] = useState(() => storage.get(draftKey(ch)) ?? '');
  const [rejected, setRejected] = useState(false);
  const [lastSent, setLastSent] = useState(0);
  const [fresh, setFresh] = useState(0);
  const [shown, setShown] = useState<Set<string>>(new Set());
  const [rulesOk, setRulesOk] = useState(chatRulesAccepted());
  const feed = useRef<HTMLDivElement>(null);
  const atBottom = useRef(true);
  const listRef = useRef(list);
  listRef.current = list;
  const now = useNow(500);
  useActionBar({ hidden: true });

  const load = async (before?: string) => {
    setErr(false);
    try {
      const r = await app.call('getChat', { params: { channel: ch }, query: { limit: CHAT_PAGE, ...(before ? { before } : {}) } });
      setMore(r.items.length >= CHAT_PAGE);
      setMute(r.muteUntil);
      setTg(r.tgGroupUrl);
      const el = feed.current;
      const h = el?.scrollHeight ?? 0;
      setList((l) => mergeMessages(l, r.items));
      setLoaded(true);
      // Подгрузка вверх не сдвигает то, что игрок читает.
      if (before && el) requestAnimationFrame(() => { el.scrollTop += el.scrollHeight - h; });
    } catch (e) {
      if (e instanceof ApiCallError && e.code === 'NOT_IN_COOP') { app.nav.pop(); return; }
      setErr(true);
    }
  };
  useEffect(() => { void load(); }, []);

  // WS: новые сообщения и скрытие (§19).
  useEffect(() => onWs((ev) => {
    if (ev.t === 'chat.message' && ev.d.channel === ch) {
      setList((l) => mergeMessages(l, [ev.d]));
      if (!atBottom.current && ev.d.author.publicId !== me) setFresh((n) => n + 1);
    } else if (ev.t === 'chat.hidden' && ev.d.channel === ch) {
      setList((l) => l.map((b) => (b.m.id === ev.d.id ? { ...b, m: { ...b.m, hidden: ev.d.reason } } : b)));
    }
  }), [ch]);

  // Новое внизу видно, если игрок и так был внизу; иначе — плашка «↓ N новых».
  useLayoutEffect(() => {
    const el = feed.current;
    if (el && atBottom.current) el.scrollTop = el.scrollHeight;
  }, [list.length, loaded]);
  const onScroll = () => {
    const el = feed.current;
    if (!el) return;
    atBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    if (atBottom.current) setFresh(0);
    if (el.scrollTop < 60 && more && loaded && list[0]) void load(list[0].m.id);
  };
  const toBottom = () => { const el = feed.current; if (el) el.scrollTop = el.scrollHeight; atBottom.current = true; setFresh(0); };

  // Черновик — при каждом вводе и при уходе (§11.2).
  useEffect(() => { storage.set(draftKey(ch), text); }, [text]);

  const send = async (b: Bubble) => {
    setList((l) => mergeMessages(l, [b.m], 'sending'));
    try {
      const r = await app.call('postChat', { params: { channel: ch }, body: { body: b.m.body, clientMsgId: b.m.clientMsgId!, ...(b.m.replyTo ? { replyTo: b.m.replyTo } : {}) } });
      setList((l) => mergeMessages(l, [r]));
    } catch (e) {
      const code = e instanceof ApiCallError ? e.code : null;
      if (code === 'NETWORK' || code === 'TIMEOUT') { setList((l) => l.map((x) => (x.m.clientMsgId === b.m.clientMsgId ? { ...x, status: 'queued', queuedAt: x.queuedAt ?? Date.now() } : x))); return; }
      setList((l) => l.filter((x) => x.m.clientMsgId !== b.m.clientMsgId));
      if (code === 'CHAT_REJECTED' || code === 'BODY_INVALID') { setText(b.m.body); setRejected(true); }
      else if (code === 'CHAT_MUTED') { const u = (e as ApiCallError).details?.['until']; setMute(typeof u === 'string' ? u : new Date(Date.now() + 3_600_000).toISOString()); setText(b.m.body); }
      else if (code === 'RATE_LIMITED') { setText(b.m.body); setLastSent(Date.now() + ((e as ApiCallError).retryAfterS ?? 2) * 1000 - CHAT_GAP_MS); }
      else app.handleError(e);
    }
  };
  // Связь вернулась — отправляем очередь по порядку; 30 с без сети — «!».
  useEffect(() => {
    if (online.value) for (const b of listRef.current.filter((x) => x.status === 'queued')) void send(b);
  }, [online.value]);
  useEffect(() => {
    if (list.some((b) => b.status === 'queued' && b.queuedAt && now - b.queuedAt > OFFLINE_FAIL_MS)) {
      setList((l) => l.map((b) => (b.status === 'queued' && b.queuedAt && now - b.queuedAt > OFFLINE_FAIL_MS ? { ...b, status: 'failed' } : b)));
    }
  }, [now]);

  const submit = (body = text) => {
    const t = body.trim();
    if (!t || t.length > CHAT_MAX) return;
    if (isBlocked(t)) { setRejected(true); return; }
    const c = coopBrief.value;
    const mine: ChatMessage = {
      id: `local-${uuid7()}`, channel: ch, author: { publicId: me, displayName: player.value?.displayName ?? '', role: c?.role ?? null },
      body: t, replyTo: replyTo.value?.id ?? null, createdAt: nowIso(), hidden: null, clientMsgId: uuid7(),
    };
    setText('');
    setRejected(false);
    replyTo.value = null;
    setLastSent(Date.now());
    atBottom.current = true;
    if (!online.value) { setList((l) => mergeMessages(l, [mine], 'queued').map((b) => (b.m.clientMsgId === mine.clientMsgId ? { ...b, queuedAt: Date.now() } : b))); return; }
    void send({ m: mine, status: 'sending' });
  };

  const muteUntil = muteLocal.value && Date.parse(muteLocal.value) > now ? muteLocal.value : mute && Date.parse(mute) > now ? mute : null;
  const wait = Math.max(0, lastSent + CHAT_GAP_MS - now);
  const byId = new Map(list.map((b) => [b.m.id, b.m]));
  const visible = list.filter((b) => b.m.hidden !== 'author' || b.m.author.publicId === me);
  // Появляются только новые сообщения: всё, что было при первой загрузке, стоит сразу (без «каскада» на входе).
  const seenAtLoad = useRef<Set<string> | null>(null);
  if (loaded && !seenAtLoad.current) seenAtLoad.current = new Set(visible.map((b) => b.m.clientMsgId ?? b.m.id));
  const open = (m: ChatMessage, failed: boolean) => {
    app.haptic('light');
    if (failed) {
      app.nav.openSheet('MOD-CONFIRM', { title: tr('CL-CHAT-FAILED'), risky: tr('CL-CHAT-DELETE'), safe: tr('CL-CHAT-RETRY'),
        onRisky: () => setList((l) => l.filter((x) => x.m.clientMsgId !== m.clientMsgId)),
        onSafe: () => { void send({ m, status: 'sending' }); } });
      return;
    }
    app.nav.openSheet('MOD-CHAT-MESSAGE', { msg: m });
  };
  return (
    <>
      <ScreenTitle right={tg ? <button class="chip" data-testid="chat-tg" aria-label={tr('CL-TG-OPEN')} onClick={() => app.wa?.openTelegramLink?.(tg)}>{tr('CL-TG-OPEN')}</button> : null}>
        {tr('COOP-CHAT-TITLE')}
      </ScreenTitle>
      <div class="chat" data-testid="SCR-COOP-CHAT">
        {myCoop.value?.pinnedMessage ? <PinnedLine text={myCoop.value.pinnedMessage} /> : null}
        <div class="chat-feed" ref={feed} onScroll={onScroll} data-testid="chat-feed">
          {!loaded && !err ? <Skeleton h={48} n={5} /> : null}
          {err && !loaded ? <div class="empty">{tr('CL-CHAT-LOAD-FAIL')}<button class="btn small outline" onClick={() => { void load(); }}>{tr('CL-CHAT-RETRY')}</button></div> : null}
          {loaded && more ? <div class="chat-more small muted">{tr('CL-CHAT-OLDER')}</div> : null}
          {loaded && !visible.length ? (
            <div class="empty" data-testid="chat-empty">
              {tr('COOP-CHAT-EMPTY')}
              <div class="col" style={{ gap: 6, width: '100%' }}>{QUICK_REPLIES.slice(0, 3).map((k) => <button key={k} class="btn outline" disabled={!!muteUntil} onClick={() => submit(tr(k))}>{tr(k)}</button>)}</div>
            </div>
          ) : null}
          {visible.map((b) => {
            const m = b.m;
            const own = m.author.publicId === me;
            const reported = reportedLocal.value.has(m.id) && !shown.has(m.id);
            const quoted = m.replyTo ? byId.get(m.replyTo) : null;
            return (
              <div key={m.clientMsgId ?? m.id} class={`bubble-row ${own ? 'own' : ''} ${seenAtLoad.current && !seenAtLoad.current.has(m.clientMsgId ?? m.id) ? 'fresh' : ''}`} data-testid={`msg-${m.id}`}>
                <button
                  class={`bubble ${m.hidden ? 'hidden' : ''} ${b.status}`}
                  onPointerDown={(e) => { const t = setTimeout(() => open(m, b.status === 'failed'), 400); const up = () => { clearTimeout(t); e.currentTarget?.removeEventListener('pointerup', up); }; e.currentTarget.addEventListener('pointerup', up); e.currentTarget.addEventListener('pointerleave', up); }}
                  onContextMenu={(e) => { e.preventDefault(); open(m, b.status === 'failed'); }}
                  onClick={() => { if (b.status === 'failed') open(m, true); }}
                >
                  {!own ? <span class="who small">{m.author.displayName}{m.author.role && m.author.role !== 'member' ? <span class="role"> · {tr(m.author.role === 'chairman' ? 'COOP-ROLE-CHAIR' : 'COOP-ROLE-DEPUTY')}</span> : null}</span> : null}
                  {quoted ? <span class="quote small">{quoted.author.displayName}: {quoted.body.slice(0, 60)}</span> : null}
                  {reported ? <span class="muted small">{tr('CL-CHAT-HIDDEN')} · <span class="link" onClick={(e) => { e.stopPropagation(); setShown(new Set([...shown, m.id])); }}>{tr('CL-CHAT-SHOW')}</span></span>
                    : m.hidden && own ? <><span class="text">{m.body}</span><span class="muted small">{tr(m.hidden === 'filter' ? 'CL-CHAT-HIDDEN-FILTER' : m.hidden === 'moderator' ? 'CL-CHAT-HIDDEN-MOD' : 'CL-CHAT-DELETED')}</span></>
                    : m.hidden ? <span class="muted small">{tr('CL-CHAT-HIDDEN')}</span>
                    : <span class="text">{m.body}</span>}
                  <span class="meta small">{mskTime(Date.parse(m.createdAt))}{b.status === 'sending' || b.status === 'queued' ? ' 🕓' : b.status === 'failed' ? ' !' : ''}</span>
                </button>
              </div>
            );
          })}
        </div>
        {fresh > 0 ? <button class="chat-new" data-testid="chat-new" onClick={toBottom}>{tr('CL-CHAT-NEW', { n: fresh })}</button> : null}
        <div class="chat-input">
          {!rulesOk ? <ChatRulesConsent onDone={() => setRulesOk(true)} /> : null}
          {replyTo.value ? <div class="reply-bar small"><span style={{ flex: 1 }} class="ellipsis">{tr('CL-CHAT-REPLY-TO', { name: replyTo.value.author.displayName })}: {replyTo.value.body}</span><button aria-label={tr('UI-BTN-CLOSE')} onClick={() => { replyTo.value = null; }}>✕</button></div> : null}
          {!rulesOk ? null : muteUntil ? <div class="banner" data-testid="chat-muted">{tr('CL-CHAT-MUTED', { time: mskTime(Date.parse(muteUntil)) })}</div> : (
            <>
              <div class="row" style={{ gap: 6, alignItems: 'flex-end' }}>
                <details class="quick">
                  <summary aria-label={tr('CL-CHAT-QUICK')}>
                    {/* Облачко реплики — кодом: в наборе иконок его нет, эмодзи в интерфейсе запрещены (ux/13). */}
                    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden><path d="M4 5h16v10H9l-4 4v-4H4z" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round" /></svg>
                  </summary>
                  <div class="quick-list">{QUICK_REPLIES.map((k) => <button key={k} class="chip" onClick={(e) => { (e.currentTarget.closest('details') as HTMLDetailsElement).open = false; submit(tr(k)); }}>{tr(k)}</button>)}</div>
                </details>
                <textarea class="input chat-text" data-testid="chat-text" rows={1} maxLength={CHAT_MAX} placeholder={tr('COOP-CHAT-PLACEHOLDER')} value={text}
                  onInput={(e) => { setText((e.target as HTMLTextAreaElement).value); setRejected(false); }}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && !('ontouchstart' in window)) { e.preventDefault(); if (!wait) submit(); } }} />
                <button class="btn accent chat-send" data-testid="chat-send" aria-label={tr('CL-CHAT-SEND')} disabled={!text.trim() || wait > 0} onClick={() => submit()}>
                  {wait > 0 ? Math.ceil(wait / 1000) : '➤'}
                </button>
              </div>
              {rejected ? <span class="small danger-text" data-testid="chat-rejected">{tr('CL-CHAT-REJECTED')}</span> : null}
              {text.length > CHAT_COUNTER_FROM ? <span class="small muted">{text.length} / {CHAT_MAX}</span> : null}
              {!online.value ? <span class="small muted">{tr('CL-CHAT-OFFLINE')}</span> : null}
            </>
          )}
          {rulesOk ? <span class="small muted chat-rules">{tr('COOP-CHAT-RULES')}</span> : null}
        </div>
      </div>
    </>
  );
}

/** Закреплённое (ux/07 §11.1): одна строка, тап разворачивает. */
function PinnedLine(p: { text: string }) {
  const [open, setOpen] = useState(false);
  return <button class={`chat-pinned small ${open ? 'open' : ''}`} data-testid="chat-pinned" onClick={() => setOpen(!open)}>📌 {p.text}</button>;
}

/** Строка согласия с правилами чата при первом входе (ВР-69 Ч3): «Читать правила» и «Принимаю». */
export function ChatRulesConsent(p: { onDone: () => void }) {
  const app = useApp();
  const [busy, setBusy] = useState(false);
  const version = chatRulesVersion();
  const url = legalUrls()['chat_rules'];
  const accept = async () => {
    if (!version) { p.onDone(); return; }
    setBusy(true);
    try {
      await app.call('acceptConsent', { body: { docId: 'chat_rules', version } });
      markChatRulesAccepted(version);
      p.onDone();
    } catch (e) { app.handleError(e); } finally { setBusy(false); }
  };
  return (
    <div class="chat-consent col" data-testid="chat-consent">
      <span class="small">{tr('CL-CHAT-CONSENT')}</span>
      <div class="row" style={{ gap: 8 }}>
        {url ? <button class="btn small outline" style={{ width: 'auto' }} data-testid="chat-rules-open" onClick={() => app.wa?.openLink?.(url)}>{tr('CL-CHAT-RULES-READ')}</button> : null}
        <button class="btn small accent" style={{ flex: 1 }} data-testid="chat-rules-accept" disabled={busy || !online.value} onClick={accept}>{tr('CL-LEGAL-ACCEPT')}</button>
      </div>
    </div>
  );
}

const REPORT_REASONS = ['offensive', 'spam', 'scam', 'other'] as const;

export function ChatMessageSheet({ entry }: SheetProps) {
  const app = useApp();
  const m = entry.params['msg'] as ChatMessage;
  const [reporting, setReporting] = useState(false);
  const [busy, setBusy] = useState(false);
  const me = player.value?.publicId;
  const own = m.author.publicId === me;
  const lead = isLeader(myCoop.value?.myRole ?? coopBrief.value?.role);
  const close = () => app.nav.popSheet();
  const run = async (fn: () => Promise<unknown>, ok?: string) => {
    setBusy(true);
    try { await fn(); if (ok) showToast(ok, 'success'); close(); }
    catch (e) {
      const code = e instanceof ApiCallError ? e.code : null;
      if (code === 'ALREADY_REPORTED') { showToast(tr('CL-CHAT-ALREADY-REPORTED')); close(); }
      else app.handleError(e);
    } finally { setBusy(false); }
  };
  const report = (reason: typeof REPORT_REASONS[number]) => run(async () => {
    await app.call('reportChat', { params: { channel: m.channel, id: m.id }, body: { reason } });
    reportedLocal.value = new Set([...reportedLocal.value, m.id]);
  }, tr('CL-CHAT-REPORTED'));
  const pinKey = useRef(app.idem());
  const pin = () => run(async () => {
    const c = myCoop.value;
    if (!c) return;
    const r = await app.call('updateCoop', { params: { coopId: c.coopId }, body: { pinnedMessage: m.body.slice(0, 300) }, idempotencyKey: pinKey.current });
    pinKey.current = app.idem();
    myCoop.value = r;
  }, tr('CL-CHAT-PINNED'));
  const mute = (s: number) => run(() => app.call('muteInChat', { params: { channel: m.channel }, body: { publicId: m.author.publicId, durationS: s } }), tr('CL-CHAT-MUTE-DONE', { name: m.author.displayName }));
  const off = busy || !online.value;
  const actions: { text: string; onClick: () => unknown; disabled?: boolean; tone?: 'outline' | 'danger'; testId?: string }[] = reporting
    ? REPORT_REASONS.map((r) => ({ text: tr(`CL-CHAT-REASON-${r.toUpperCase()}`), tone: 'outline' as const, testId: `report-${r}`, disabled: off, onClick: () => report(r) }))
    : [
      { text: tr('CL-CHAT-REPLY'), testId: 'msg-reply', onClick: () => { replyTo.value = m; close(); } },
      { text: tr('CL-CHAT-COPY'), tone: 'outline', testId: 'msg-copy', onClick: () => { void navigator.clipboard?.writeText(m.body).then(() => showToast(tr('CL-CHAT-COPIED'))).catch(() => {}); close(); } },
      ...(!own ? [{ text: tr('UI-NAV-PROFILE'), tone: 'outline' as const, onClick: () => { app.nav.closeSheets(); app.nav.push('SCR-GARAGE-VISIT', { publicId: m.author.publicId }); } }] : []),
      ...(!own ? [{ text: tr('COOP-CHAT-REPORT'), tone: 'outline' as const, testId: 'msg-report', onClick: () => setReporting(true) }] : []),
      ...(lead && !m.hidden ? [{ text: tr('CL-CHAT-PIN'), tone: 'outline' as const, testId: 'msg-pin', disabled: off, onClick: pin }] : []),
      ...(own || lead ? [{ text: tr('CL-CHAT-DELETE'), tone: 'danger' as const, testId: 'msg-delete', disabled: off, onClick: () => run(() => app.call('deleteChat', { params: { channel: m.channel, id: m.id } })) }] : []),
      ...(!own && lead && m.author.role !== 'chairman' ? [
        { text: tr('CL-CHAT-MUTE-1H'), tone: 'outline' as const, testId: 'msg-mute-1h', disabled: off, onClick: () => mute(3600) },
        { text: tr('CL-CHAT-MUTE-24H'), tone: 'outline' as const, disabled: off, onClick: () => mute(86_400) },
      ] : []),
    ];
  return (
    <Sheet entry={entry} title={reporting ? tr('COOP-CHAT-REPORT') : m.author.displayName} actions={actions} testId="MOD-CHAT-MESSAGE">
      <p class="small" style={{ margin: 0 }}>{m.body.slice(0, 200)}</p>
    </Sheet>
  );
}

/** MOD-TG-GROUP (ux/07 §12): председатель или заместитель вставляет ссылку-приглашение; игра группой не управляет. */
export function TgGroupSheet({ entry }: SheetProps) {
  const app = useApp();
  const c = myCoop.value;
  const [url, setUrl] = useState('');
  const [steps, setSteps] = useState(false);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const linked = c?.tgGroupUrl ?? null;
  const norm = normalizeTgInvite(url);
  const save = (u: string | null) => { if (myCoop.value) myCoop.value = { ...myCoop.value, tgGroupUrl: u }; };
  const bind = async () => {
    if (!c || !norm) return;
    setBusy(true);
    try {
      const r = await app.call('setTgGroup', { params: { coopId: c.coopId }, body: { url: norm } });
      save(r.tgGroupUrl);
      app.haptic('success');
      showToast(tr('CL-TG-LINKED'), 'success');
      setEditing(false);
      setUrl('');
    } catch (e) {
      const code = e instanceof ApiCallError ? e.code : null;
      if (code === 'URL_INVALID') setNote(tr('CL-TG-URL-INVALID'));
      else if (code === 'FORBIDDEN_ROLE') setNote(tr('CL-TG-FORBIDDEN'));
      else app.handleError(e);
    } finally { setBusy(false); }
  };
  const unbind = () => app.nav.openSheet('MOD-CONFIRM', {
    title: tr('CL-TG-UNLINK-Q'), risky: tr('CL-TG-UNLINK'),
    onRisky: async () => {
      if (!c) return;
      try { await app.call('deleteTgGroup', { params: { coopId: c.coopId } }); save(null); showToast(tr('CL-TG-UNLINKED')); }
      catch (e) { app.handleError(e); }
    },
  });
  // «Вставить» — только DOM-кнопка: чтение буфера требует жеста пользователя (ux/12 §5.6).
  const paste = async () => {
    try { setUrl((await navigator.clipboard.readText()).trim()); setNote(null); }
    catch { input.current?.focus(); setNote(tr('CL-TG-PASTE-HINT')); }
  };
  const off = busy || !online.value;
  const actions = linked && !editing
    ? [
      { text: tr('CL-TG-OPEN'), testId: 'tg-open', onClick: () => app.wa?.openTelegramLink?.(linked) },
      { text: tr('CL-TG-REPLACE'), tone: 'outline' as const, testId: 'tg-replace', onClick: () => setEditing(true) },
      { text: tr('CL-TG-UNLINK'), tone: 'danger' as const, testId: 'tg-unlink', disabled: off, onClick: unbind },
    ]
    : norm
      ? [{ text: tr('CL-TG-BIND'), testId: 'tg-bind', disabled: off, onClick: bind }, { text: tr('CL-TG-CLEAR'), tone: 'outline' as const, onClick: () => setUrl('') }]
      : [{ text: tr('CL-TG-HOW'), testId: 'tg-how', onClick: () => setSteps(!steps) }];
  return (
    <Sheet entry={entry} title={tr('CL-TG-TITLE')} actions={actions} testId="MOD-TG-GROUP">
      <div class="col">
        {linked && !editing ? <div class="banner pos" data-testid="tg-linked">{tr('CL-TG-LINKED')}</div> : (
          <>
            {steps || !url ? (
              <ol class="tg-steps small" data-testid="tg-steps">
                <li>{tr('CL-TG-STEP-1')}</li><li>{tr('CL-TG-STEP-2')}</li><li>{tr('CL-TG-STEP-3')}</li>
              </ol>
            ) : null}
            <div class="input-wrap">
              <input ref={input} class="input" data-testid="tg-url" inputMode="url" placeholder="t.me/+…" value={url} onInput={(e) => { setUrl((e.target as HTMLInputElement).value); setNote(null); }} />
              <button class="btn small outline paste" data-testid="tg-paste" onClick={paste}>{tr('CL-TG-PASTE')}</button>
            </div>
            {url && !norm ? <span class="small danger-text" data-testid="tg-bad">{tr('CL-TG-BAD-LINK')}</span> : null}
          </>
        )}
        {note ? <span class="small danger-text">{note}</span> : null}
        <span class="small muted">{tr('CL-TG-NOT-MODERATED')}</span>
      </div>
    </Sheet>
  );
}
